import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../environments/environment';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../core/interceptor/language.service';
import { InputComponent } from '../shared/components/input/input.component';
import { ButtonComponent } from '../shared/components/button/button.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  private endpointPrefix = environment.apiUrl;
  credentials!: FormGroup;
  loading: boolean = false;
  errorMsg: string = '';
  currentYear: number = new Date().getFullYear();

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private http: HttpClient,
    private translate: TranslateService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.credentials = this.fb.group({
      username: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(4)]],
    });
    this.languageService.initializeTranslation();
  }

  get emailError(): string | null {
    const control = this.credentials?.get('username');
    if (!control || !control.touched || !control.invalid) {
      return null;
    }
    if (control.hasError('required')) {
      return this.translate.instant('LOGIN.EMAIL_REQUIRED');
    }
    if (control.hasError('email') || control.hasError('pattern')) {
      return this.translate.instant('LOGIN.EMAIL_INVALID');
    }
    return null;
  }

  get passwordError(): string | null {
    const control = this.credentials?.get('password');
    if (!control || !control.touched || !control.invalid) {
      return null;
    }
    if (control.hasError('required') || control.hasError('minlength')) {
      return this.translate.instant('LOGIN.PASSWORD_REQUIRED');
    }
    return null;
  }

  onSubmit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    if (!this.credentials.valid) {
      this.credentials.markAllAsTouched();
      return;
    }

    localStorage.clear();
    this.loading = true;
    this.errorMsg = '';
    const credentialsValue = { ...this.credentials.value };
    credentialsValue.username = credentialsValue.username.trim();

    this.http
      .post(this.endpointPrefix + '/authenticate', credentialsValue)
      .subscribe({
        next: (res: any) => {
          const token = res.id_token;
          localStorage.setItem('authenticationToken', token);
          this.http
            .get<any>(`${this.endpointPrefix}/account`)
            .subscribe({
              next: (account: any) => {
                localStorage.setItem('currentUser', JSON.stringify(account));
                const queryParams: any = {};
                if (account.authorities && account.authorities.includes('ROLE_JUBOO_AGENT')) {
                  queryParams.customerId = account.customerId;
                }
                this.router
                  .navigate(['/backoffice/requestlist'], { queryParams, queryParamsHandling: 'merge' })
                  .finally(() => location.reload());
              },
              error: () => {
                this.errorMsg = this.translate.instant('LOGIN.BAD_CREDENTIALS');
                this.loading = false;
              }
            });
        },
        error: () => {
          this.errorMsg = this.translate.instant('LOGIN.BAD_CREDENTIALS');
          this.loading = false;
        }
      });
  }
}
